import React from 'react';

import {
  TCardPosition,
  TCardSlotPosition,
  TCardLayoutSlot,
  TCard,
  CardId,
} from '@/console/features/card-layout/card-layout.types';
import { renderCards } from '@/console/features/card-layout/components/card/card-factory';
import { useCardLayout } from '@/console/features/card-layout/context/card-layout-context';
import { generateGridTemplate } from '@/console/features/card-layout/utils/generate-grid-template';
import { TPermissionAction } from '@/shared/features/permissions/actions';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';

import { CardContainer, GridContainer, Slot } from './card-layout.styled';
import { useCardDrag } from './hooks/use-card-drag/use-card-drag';
import { getCardsInSlot, handleCardDrop } from './utils/card-swap';
import { isValidDropZone } from './utils/validation';

const CARD_READ_ACTIONS: Partial<Record<CardId, TPermissionAction>> = {
  [CardId.PROJECTS]: 'project:readProject',
  [CardId.DEVICES]: 'device:readDevice',
  [CardId.PIPELINES]: 'pipeline:readPipeline',
  [CardId.GROUPS]: 'resourceGroup:readResourceGroup',
};

export const CardLayout = () => {
  const { cards, setCards } = useCardLayout();
  const { can } = usePermissionCheck();
  const visibleCards = cards.filter((card) => {
    const action = CARD_READ_ACTIONS[card.id];

    return action ? can(action) : true;
  });
  const {
    draggedCard,
    draggedOverCard,
    resetDragStates,
    handleDragStart,
    handleDragOverCard,
  } = useCardDrag(visibleCards);

  const gridTemplate = generateGridTemplate(visibleCards);
  const isSingleCard = visibleCards?.length === 1;

  // Handler functions
  const handleDrop = (slot: TCardLayoutSlot): void => {
    if (!draggedCard || !isValidDropZone(draggedCard.id, slot, visibleCards)) {
      resetDragStates();
      return;
    }

    setCards((prevCards) =>
      handleCardDrop(draggedCard, draggedOverCard, slot, prevCards),
    );
    resetDragStates();
  };

  // Helper functions for class names and validations
  const isInvalidDropZone = (slot: TCardLayoutSlot): boolean =>
    !!draggedCard && !isValidDropZone(draggedCard.id, slot, visibleCards);

  const getSlotClassName = (
    slot: TCardLayoutSlot,
    draggedOverCardPosition?: TCardPosition,
  ): string => {
    if (draggedCard?.position?.order !== TCardSlotPosition.FILL) {
      return '';
    }
    return draggedOverCardPosition?.slot === slot ? 'drag-over' : '';
  };

  const getCardClassName = (
    cardPosition: TCardPosition,
    draggedOverCardPosition?: TCardPosition,
  ): string => {
    if (draggedCard?.position?.order === TCardSlotPosition.FILL) {
      return '';
    }

    const isDragTarget =
      cardPosition.order === draggedOverCardPosition?.order &&
      cardPosition.slot === draggedOverCardPosition?.slot;

    return isDragTarget ? 'drag-over' : '';
  };

  const renderSlot = (slot: TCardLayoutSlot): React.ReactNode => {
    const cardsInSlot = getCardsInSlot(visibleCards, slot);

    if (cardsInSlot.length === 0) {
      return null;
    }

    const isInvalid = isInvalidDropZone(slot);
    const slotClassName = `
      ${getSlotClassName(slot, draggedOverCard?.position)} 
      ${isInvalid ? 'invalid-drop' : ''}
    `.trim();

    return (
      <Slot
        key={slot}
        $area={slot}
        $slotsFilled={cardsInSlot.length}
        className={slotClassName}
        onDrop={() => handleDrop(slot)}
      >
        {renderCardsForSlot(slot)}
      </Slot>
    );
  };

  const renderCardsForSlot = (slot: TCardLayoutSlot): React.ReactNode[] => {
    return visibleCards
      .filter((card) => card.position.slot === slot)
      .map((card: TCard) => (
        <CardContainer
          key={card.id}
          draggable={false}
          className={getCardClassName(card.position, draggedOverCard?.position)}
          $flexOrder={card.position.order}
          onDragStart={(e: React.DragEvent) => handleDragStart(e, card)}
          onDragOver={(e: React.DragEvent) => handleDragOverCard(e, card)}
          onDragEnd={resetDragStates}
          $isDragging={draggedCard?.id === card.id}
          data-testid={`card-${card.id}`}
        >
          {renderCards(card.id)}
        </CardContainer>
      ));
  };

  return (
    <GridContainer $template={gridTemplate} $hasGap={!isSingleCard}>
      {Object.values(TCardLayoutSlot).map(renderSlot)}
    </GridContainer>
  );
};
